import { Text } from "@ubnt/ui-components/Text";
import styled, { css } from "styled-components";
import { BREAKPOINTS } from "@ubnt/store.core/lib/constants/layout";
import { ScrollWrapper } from "@ubnt/store.core/lib/components/ScrollWrapper";
import { ScrollContainer } from "@ubnt/store.core/lib/components/ScrollWrapper/ScrollWrapper.styles";
import { IconAppStore } from "./assets/IconAppStore";

export const TitleContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const DisplayProWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  position: relative;
`;

export const RadialGradient = styled.div`
  background: radial-gradient(
    96.6% 100.25% at 51.42% -33.25%,
    #cdcfd4 3.5%,
    rgba(168, 170, 176, 0.5) 50.5%,
    rgba(205, 207, 212, 0) 100%
  );
  bottom: 234px;
  height: 100%;
  max-height: 566px;
  position: absolute;
  transform: rotate(180deg);
  width: 100%;

  @media (${BREAKPOINTS.DESKTOP_AND_DOWN}) {
    display: none;
  }
`;

export const Content = styled.div`
  width: 100%;
`;

export const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  z-index: 3;
  margin-bottom: 24px;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    flex-wrap: wrap;
    gap: 8px;
  }

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    row-gap: 4px;
  }
`;

export const Tab = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: flex-start;
  background: transparent;
  padding: 4px 0;
  outline: none;
  border: none;
  width: fit-content;
  overflow: hidden;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    font-size: 14px;
  }

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    font-size: 12px;
    padding: 0;
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      pointer-events: none;
    `}
`;

export const Label = styled.span<{
  $shouldAnimate?: boolean;
  $playbackLimit?: number;
}>`
  color: transparent;
  background: linear-gradient(90deg, #212427 50%, #c2c6cb 50%);
  background-position: 100%;
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;

  &:hover {
    color: #212427;
  }

  ${({ $shouldAnimate, $playbackLimit }) =>
    $shouldAnimate &&
    css`
      animation: textProgress ${$playbackLimit}s linear;
      animation-fill-mode: forwards;

      &:hover {
        color: transparent !important;
      }
    `}

  @keyframes textProgress {
    to {
      background-position: 0;
    }
  }
`;

export const ButtonWrapper = styled.div`
  min-width: fit-content;
`;

export const TabScrollContainer = styled(ScrollWrapper)<{
  $itemsCount: number;
  $columnByDefault?: boolean;
}>`
  margin: 0 auto 36px;
  padding: 2px 16px;

  @media (${BREAKPOINTS.TABLET_AND_DOWN}) {
    margin: 0 auto 24px;
  }

  ${ScrollContainer} {
    display: flex;
    gap: 8px;
    margin-top: 0;
    margin-bottom: 0;
    padding: 2px 0;
    width: 100%;
  }

  .scroll-container-item {
    min-width: fit-content;
  }
`;

export const DisplayContainer = styled.div`
  display: flex;
  justify-content: center;
  max-height: 928px;
  max-width: 100vw;
  padding: 0 16px;
  width: 100%;
  position: relative;
`;

export const DisplayBackground = styled.div`
  aspect-ratio: 1388/928;
  max-height: 928px;
  max-width: 1388px;
  position: relative;
  width: 100%;
`;

export const AbsoluteContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
`;

export const StyledDisplay = styled.img`
  aspect-ratio: 1388 / 928;
  height: 100%;
  object-fit: unset;
  position: absolute;
  max-width: 100%;
  z-index: 5;
  overflow: hidden;
`;

export const ImageLoader = styled.div<{
  $scale: number;
}>`
  height: auto;
  aspect-ratio: 1353 / 762;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 6;
  margin: ${({ $scale }) => `${$scale * 20}px ${$scale * 18}px 0`};
  max-width: calc(100% - 36 * ${({ $scale }) => $scale}px);
  background-color: ${({ theme }) => theme.colors.white};
`;

export const StyledVideo = styled.video<{ $scale: number }>`
  aspect-ratio: 1353 / 762;
  height: auto;
  max-width: 100%;
  object-fit: unset;
  background-color: ${({ theme }) => theme.colors.white};
  margin: ${({ $scale }) => `${$scale * 20}px ${$scale * 18}px 0`};
  width: calc(100% - ${({ $scale }) => $scale * 36}px);
  z-index: 7;
  position: absolute;
  background-color: ${({ theme }) => theme.colors.white};
`;

export const StyledIphoneContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 80px 0 70px;
  overflow: hidden;
  position: relative;

  @media (${BREAKPOINTS.DESKTOP_AND_DOWN}) {
    margin: 24px 0 16px;
  }

  @media (${BREAKPOINTS.MOBILE_AND_DOWN}) {
    margin: 0 0 16px;
  }
`;

export const PhoneRadialGradient = styled.div`
  background: radial-gradient(
    96.6% 100.25% at 51.42% -33.25%,
    #cdcfd4 3.5%,
    rgba(168, 170, 176, 0.5) 50.5%,
    rgba(205, 207, 212, 0) 100%
  );
  bottom: 10%;
  height: 100%;
  max-height: 780px;
  position: absolute;
  transform: rotate(180deg);
  width: 100%;
`;

export const StyledTab = styled.button`
  background: transparent;
  border: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  justify-content: space-between;
  max-width: fit-content;
  min-width: 123px;
  outline: none;
  width: 100%;

  @media (${BREAKPOINTS.DESKTOP_AND_DOWN}) {
    min-width: fit-content;
    max-width: unset;
  }
`;

export const TabContent = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 10px;
  width: fit-content;
`;

export const TabText = styled(Text)<{ $isActive: boolean }>`
  color: #50565e;
  font-weight: 500;
  line-height: 16px;
  text-align: start;

  @media (${BREAKPOINTS.DESKTOP_AND_DOWN}) {
    font-size: 12px;
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      color: #000;
    `}
`;

export const IphoneGrid = styled.div`
  display: flex;
  gap: 32px;
  z-index: 2;

  @media (${BREAKPOINTS.DESKTOP_AND_DOWN}) {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  @media (${BREAKPOINTS.TABLET_AND_DOWN}) {
    gap: 0;
  }
`;

export const IphoneGridContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 40px 0 120px;
  width: 100%;

  @media (${BREAKPOINTS.DESKTOP_AND_DOWN}) {
    gap: 12px;
    max-width: 100%;
    padding: 24px 0 0;
  }
`;

export const IphoneContentTitle = styled.h2`
  color: #212427;
  font-size: 30px;
  font-weight: 700;
  line-height: normal;
  margin: 0;
  text-wrap: balance;

  @media (${BREAKPOINTS.DESKTOP_AND_DOWN}) {
    text-align: center;
  }
`;

export const IphoneContentDescription = styled.p`
  color: #808893;
  font-size: 16px;
  font-style: normal;
  line-height: 24px;
  margin: 0;

  @media (${BREAKPOINTS.DESKTOP_AND_DOWN}) {
    text-align: center;
  }
`;

export const MobileAppLinksWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;

  @media (${BREAKPOINTS.DESKTOP_AND_DOWN}) {
    justify-content: center;
  }
`;

export const MobileAppLink = styled.a`
  background: transparent;
  display: flex;
`;

export const MobileAppImg = styled.img`
  height: 40px;
  width: 134.55px;
`;

export const PhoneContainer = styled.div`
  aspect-ratio: 805/890;
  display: flex;
  height: 100%;
  justify-content: center;
  max-height: 890px;
  max-width: 805px;
  position: relative;

  @media (max-width: 1520px) {
    margin-right: 15%;
  }

  @media (max-width: 1396px) {
    margin-right: 25%;
  }

  @media (${BREAKPOINTS.DESKTOP_AND_DOWN}) {
    margin-left: calc(430px * 0.15);
    margin-right: 0;
    width: 100%;
  }

  @media (${BREAKPOINTS.MOBILE_AND_DOWN}) {
    margin: 0 12px 0 8%;
  }
`;

export const PhoneMockup = styled.img`
  height: 100%;
  max-height: 889px;
  max-width: 430px;
  object-fit: contain;
  position: relative;
  width: 100%;
  z-index: 4;
`;

export const StyledAppStoreIcon = styled(IconAppStore)`
  width: 119.66px;
`;

export const StyledPhoneVideo = styled.video<{ $scale: number }>`
  aspect-ratio: 427 / 874;
  border-radius: 64px;
  height: auto;
  left: 2%;
  position: absolute;
  top: 1.8%;
  width: calc(100% - 16px * ${({ $scale }) => $scale});

  @media (${BREAKPOINTS.TABLET_AND_DOWN}) {
    border-radius: 32px;
  }
`;

export const PhoneCaseWrapper = styled.div<{
  $isCurrentSlide: boolean;
  $isNextSlide: boolean;
  $scale: number;
}>`
  height: calc(880px * ${({ $scale }) => $scale});
  left: 0;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  width: calc(430px * ${({ $scale }) => $scale});

  @media (${BREAKPOINTS.MOBILE_AND_DOWN}) {
    min-height: 348px;
    min-width: 170px;
  }

  ${({ $isCurrentSlide }) =>
    $isCurrentSlide &&
    css`
      animation: slideInFromLeft 20s both infinite;
      opacity: 1;
    `}

  ${({ $isNextSlide }) =>
    $isNextSlide &&
    css`
      animation: slideInFromBehind 20s both infinite;
      opacity: 1;
    `}

  @keyframes slideInFromLeft {
    0% {
      transform: translateX(0px) scale(1);
      z-index: 2;
    }

    47.85% {
      transform: translateX(0px) scale(1);
      z-index: 2;
    }

    48.75% {
      transform: translateX(-5%) scale(1);
    }

    50% {
      transform: translateX(70%) scale(0.9);
      z-index: 1;
    }

    97.85% {
      transform: translateX(70%) scale(0.9);
      z-index: 1;
    }

    98.75% {
      transform: translateX(100%) scale(1);
    }

    100% {
      transform: translateX(0px) scale(1);
      z-index: 2;
    }
  }

  @keyframes slideInFromBehind {
    0% {
      transform: translateX(70%) scale(0.9);
      z-index: 1;
    }

    47.85% {
      transform: translateX(70%) scale(0.9);
      z-index: 1;
    }

    48.75% {
      transform: translateX(100%) scale(1);
    }

    50% {
      transform: translateX(0px) scale(1);
      z-index: 2;
    }

    97.85% {
      transform: translateX(0px) scale(1);
      z-index: 2;
    }

    98.75% {
      transform: translateX(-5%) scale(1);
    }

    100% {
      transform: translateX(70%) scale(0.9);
      z-index: 1;
    }
  }
`;

export const PhoneWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  @media (${BREAKPOINTS.TABLET_AND_DOWN}) {
    padding: 0 24px;
    min-height: 348px;
  }

  @media (${BREAKPOINTS.TABLET_AND_DOWN}) {
    padding: 0;
  }
`;

export const IconContainer = styled.div`
  align-items: center;
  background: #c2c6cb;
  border-radius: 6px;
  display: flex;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  overflow: hidden;
  position: relative;
  width: 32px;

  svg {
    color: white;
    z-index: 3;
  }
`;

export const IconFillContainer = styled.div<{ $isActive: boolean }>`
  ${({ $isActive }) =>
    $isActive &&
    css`
      animation-fill-mode: forwards;
      animation: progress 10s linear;
      background: #006fff;
      height: 100%;
      left: 0;
      position: absolute;
      transform: translateX(-100%);
      width: 100%;
      z-index: 2;

      @keyframes progress {
        0% {
          transform: translateX(-100%);
        }

        100% {
          transform: translateX(0);
        }
      }
    `}
`;

export const MobileTabWrapper = styled.button`
  background: transparent;
  border: none;
  display: flex;
  gap: 12px;
  height: 100%;
  justify-content: space-between;
  outline: none;
  width: 100%;
`;

export const MobileTabContent = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  width: fit-content;
`;

export const StyledTabLabel = styled(Text)<{ $isActive: boolean }>`
  color: ${({ $isActive }) => ($isActive ? "black" : "#50565E")};
  line-height: 16px;
  min-width: max-content;
  text-align: start;
`;
